import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Grid, Paper, Typography, Box, Card, CardContent, 
    Chip, Button, IconButton 
} from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { 
    Chart as ChartJS, ArcElement, Tooltip, Legend, 
    CategoryScale, LinearScale, BarElement 
} from 'chart.js';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

import SmartphoneIcon from '@mui/icons-material/Smartphone';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [mobileApps, setMobileApps] = useState([]);
  const firstName = (localStorage.getItem('full_name') || 'User').split(' ')[0];
  
  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${API_URL}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    
    const fetchMobileApps = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${API_URL}/setup/mobile-apps`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMobileApps(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    
    fetchStats();
    fetchMobileApps();
  }, []);

  if (!stats) return <Loading message="Loading Dashboard..." />;

  // Doughnut Data (Vibrant Colors)
  const doughnutData = {
    labels: ['At Risk', 'Completed', 'On Hold', 'On Track'],
    datasets: [
      {
        data: [
            stats.status_distribution['Overdue'] || 0, 
            stats.status_distribution['Completed'] || 0, 
            stats.status_distribution['Submitted'] || 0, 
            stats.status_distribution['In Progress'] || 0
        ],
        backgroundColor: ['#F59E0B', '#94A3B8', '#EF4444', '#10B981'],
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverOffset: 4
      },
    ],
  };

  // Clean Stat Card Component
  const StatCard = ({ title, value, subtitle, icon, color, gradient }) => (
    <Card elevation={0} sx={{ 
        height: '100%', 
        p: 1, 
        borderRadius: 4, 
        bgcolor: '#ffffff', 
        border: '1px solid #f1f5f9', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: gradient || color,
        },
        '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 14px 28px rgba(0,0,0,0.08)', borderColor: '#cbd5e1' } 
    }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
                color: '#ffffff', 
                p: 1.5, 
                borderRadius: 3, 
                background: gradient || color, 
                display: 'flex',
                boxShadow: `0 8px 16px ${color}40`,
            }}>
                {icon}
            </Box>
            <Typography variant="h3" fontWeight="800" sx={{ color: '#0f172a', letterSpacing: '-0.02em' }}>{value}</Typography>
        </Box>
        <Typography variant="h6" fontWeight="700" color="#334155" sx={{ mb: 0.5, letterSpacing: '-0.01em' }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: subtitle.includes('Risk') ? '#ef4444' : (subtitle.includes('Active') ? '#10b981' : '#64748b'), fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', p: { xs: 2, md: 3 } }}>
      
      {/* Welcome Header */}
      <Box sx={{ 
          mb: { xs: 3, md: 4 }, 
          p: { xs: 3, md: 4 }, 
          borderRadius: 4, 
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: 'white',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.15)',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight="800" sx={{ color: '#ffffff', mb: 1, fontSize: { xs: '1.75rem', md: '2.25rem' }, letterSpacing: '-0.02em' }}>
                Welcome back, {firstName} 👋
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', fontWeight: 500, maxWidth: 600 }}>
                Here's what's happening with your projects and schedules today.
            </Typography>
        </Box>
        
        {/* --- HEADER BUTTONS: User Guide + Mobile Apps --- */}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
                component="a"
                href="/manual/Pm.html" 
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<HelpCenterIcon />}
                sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        transform: 'translateY(-2px)'
                    }
                }}
            >
                User Guide
            </Button>
            {mobileApps.map((app) => {
              const getIcon = (type) => {
                if (type === 'iOS') return <AppleIcon />;
                if (type === 'Android') return <AndroidIcon />;
                return <PhoneAndroidIcon />;
              };
              return (
                <Button
                  key={app.id}
                  component="a"
                  href={app.link || '#'}
                  target={app.link ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={getIcon(app.app_type)}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        transform: 'translateY(-2px)'
                    }
                  }}
                >
                    V{app.version}
                </Button>
              );
            })}
        </Box>

        {/* Decorative background elements */}
        <Box sx={{ position: 'absolute', top: '-50%', right: '-5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(56,189,248,0.15) 0%, rgba(255,255,255,0) 70%)', zIndex: 0 }} />
        <Box sx={{ position: 'absolute', bottom: '-20%', right: '10%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, rgba(255,255,255,0) 70%)', zIndex: 0 }} />
      </Box>

      {/* Stats Row */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Projects" value={stats.total_projects} subtitle="Assigned to you" icon={<FolderIcon fontSize="medium" />} color="#3B82F6" gradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active" value={stats.active_projects} subtitle="All systems operational" icon={<CheckCircleIcon fontSize="medium" />} color="#10B981" gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="At Risk" value={stats.at_risk} subtitle="Require immediate attention" icon={<WarningIcon fontSize="medium" />} color="#F59E0B" gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Team Members" value={stats.team_members} subtitle={`${stats.utilization}% Avg Utilization`} icon={<GroupIcon fontSize="medium" />} color="#8B5CF6" gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" />
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        
        {/* Left Column: Project Performance */}
        <Grid item xs={12} lg={8}>
            <Card elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4, bgcolor: '#ffffff', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 12px 28px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' } }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight="800" color="#1e293b">Project Status Distribution</Typography>
                    <Typography variant="body2" fontWeight="600" color="#64748b">Current condition status across all active projects</Typography>
                </Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} sx={{ mb: 1.5 }}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={5}>
                                <Box sx={{ height: 200, display: 'flex', justifyContent: 'center' }}>
                                    <Doughnut data={doughnutData} options={{ cutout: '75%', plugins: { legend: { display: false } } }} />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Condition Summary</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {[
                                        { label: 'At Risk', value: stats.status_distribution['Overdue'] || 0, color: '#F59E0B', sub: 'Critical Attention' },
                                        { label: 'On Track', value: stats.status_distribution['In Progress'] || 0, color: '#10B981', sub: 'Good Condition' },
                                        { label: 'Completed', value: stats.status_distribution['Completed'] || 0, color: '#94A3B8', sub: 'Finished' },
                                    ].map((item) => (
                                        <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderRadius: 2, bgcolor: '#F8FAFC' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="bold">{item.label}</Typography>
                                                    <Typography variant="caption" color="textSecondary">{item.sub}</Typography>
                                                </Box>
                                            </Box>
                                            <Typography variant="h6" fontWeight="bold">{item.value}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>
        </Grid>

        {/* Right Column: Upcoming Schedule */}
        <Grid item xs={12} lg={4}>
          <Card elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4, bgcolor: '#ffffff', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 12px 28px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' } }}>                  
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="800" color="#1e293b">Upcoming Schedule</Typography>
                        <Typography variant="body2" fontWeight="600" color="#64748b">Project timeline monitoring</Typography>
                    </Box>
                    {stats.weekly_schedule && stats.weekly_schedule.length > 0 && (
                        <Chip label={stats.weekly_schedule.length} size="small" color="error" sx={{ fontWeight: 'bold', borderRadius: 2 }} />
                    )}
                </Box>
                
                <Paper elevation={0} sx={{ p: 0, bgcolor: 'transparent' }}>
                    {stats.weekly_schedule && stats.weekly_schedule.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {stats.weekly_schedule.map((entry) => (
                                <Card 
                                    key={entry.id} 
                                    elevation={0}
                                    sx={{ 
                                        p: 2, borderRadius: 4, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', transition: 'all 0.3s ease', cursor: 'pointer',
                                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', borderColor: '#cbd5e1', bgcolor: '#ffffff' }
                                    }}
                                    onClick={() => navigate('/datasheets', { state: { openEventId: entry.id } })}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Chip label={entry.status === 'Completed' ? 'Completed' : 'On Schedule'} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'white', color: '#EA580C', border: '1px solid #FFEDD5' }} />
                                    </Box>
                                    
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#9A3412', mb: 0.5 }}>{entry.title}</Typography>
                                    <Typography variant="caption" display="block" sx={{ color: '#C2410C', mb: 2, lineHeight: 1.4 }}>
                                        Scheduled work for project {entry.project_name} related to {entry.type_of_work} phase.
                                    </Typography>

                                    <Grid container justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="caption" display="block" sx={{ color: '#9A3412', opacity: 0.7 }}>Date</Typography>
                                            <Typography variant="caption" fontWeight="bold" sx={{ color: '#9A3412' }}>{new Date(entry.start_time).toLocaleDateString()}</Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="caption" display="block" sx={{ color: '#9A3412', opacity: 0.7 }}>Time</Typography>
                                            <Typography variant="caption" fontWeight="bold" sx={{ color: '#9A3412' }}>{new Date(entry.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                                        </Box>
                                    </Grid>
                                </Card>
                            ))}
                            <Box 
                                onClick={() => navigate('/datasheets')}
                                sx={{ p: 2, textAlign: 'center', bgcolor: '#ffffff', borderRadius: 4, border: '1px dashed #cbd5e1', cursor: 'pointer', transition: 'all 0.3s ease', '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' } }}
                            >
                                <Typography variant="button" fontWeight="800" color="#64748b" sx={{ textTransform: 'none' }}>View All Schedule</Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center', border: '2px dashed #f1f5f9', borderRadius: 4, bgcolor: '#f8fafc' }}>
                            <CheckIcon sx={{ fontSize: 48, color: '#10b981', opacity: 0.5, mb: 2 }} />
                            <Typography variant="body2" color="#64748b" fontWeight="600">No upcoming schedule items.</Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Risk Projects & Active Tickets Grid */}
      <Grid container spacing={3} sx={{ mt: 4 }}>
            {/* Risk Projects (Alerts) */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4, bgcolor: 'white', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Risk Projects</Typography>
                    <Typography variant="caption" color="textSecondary">Projects requiring immediate attention</Typography>
                </Box>
                {stats.alerts && stats.alerts.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {stats.alerts.map((alert, index) => (
                            <Paper 
                                key={index} elevation={0} 
                                sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'white', transition: 'all 0.2s', cursor: 'pointer', '&:hover': { boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' } }}
                                onClick={() => navigate(`/projects/${alert.id}`)}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">Project Alert</Typography>
                                    <Chip label="Critical" size="small" sx={{ bgcolor: '#DC2626', color: 'white', fontWeight: 'bold', borderRadius: 1.5, height: 24 }} />
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Chip label="Overdue" size="small" variant="outlined" sx={{ color: '#DC2626', borderColor: '#DC2626', fontWeight: 'bold', borderRadius: 1.5, height: 22, fontSize: '0.7rem' }} />
                                </Box>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{alert.message}</Typography>
                                <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" fontWeight="bold" color="textSecondary">Due Date:</Typography>
                                    <Typography variant="caption" color="textSecondary">{alert.date}</Typography>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
                         <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                         <Typography variant="body2" color="textSecondary">No projects currently at risk.</Typography>
                    </Paper>
                )}
              </Paper>
            </Grid>

            {/* Active Tickets */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4, bgcolor: 'white', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Active Tickets</Typography>
                    <Typography variant="caption" color="textSecondary">Your assigned ticket queue</Typography>
                </Box>
                 {stats.my_tickets && stats.my_tickets.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {stats.my_tickets.map((ticket) => (
                            <Paper 
                                key={ticket.id} elevation={0} 
                                sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'white', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', borderColor: '#3B82F6' } }}
                                onClick={() => navigate(`/tickets/${ticket.id}`)}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">{ticket.title}</Typography>
                                    <Chip 
                                        label={ticket.status} size="small" 
                                        sx={{ 
                                            bgcolor: ticket.status === 'In Progress' ? '#2563EB' : ticket.status === 'Done' ? '#10B981' : ticket.status === 'Reopen' ? '#DC2626' : '#D97706', 
                                            color: 'white', fontWeight: 'bold', borderRadius: 1.5, height: 24 
                                        }} 
                                    />
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Chip label={ticket.type} size="small" variant="outlined" sx={{ color: '#EA580C', borderColor: '#EA580C', fontWeight: 'bold', borderRadius: 1.5, height: 22, fontSize: '0.7rem' }} />
                                </Box>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
                                     ID: #{String(ticket.id).padStart(5, '0')} • Assigned to you
                                </Typography>
                                <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" fontWeight="bold" color="textSecondary">Action Required</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#2563EB' }}>
                                        <Typography variant="caption" fontWeight="bold">View Ticket</Typography>
                                        <ArrowForwardIcon sx={{ fontSize: 12 }} />
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
                         <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                         <Typography variant="body2" color="textSecondary">No active tickets.</Typography>
                    </Paper>
                )}
              </Paper>
            </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;